import React, { useEffect, useMemo, useState } from 'react';
import AdminService from '../../../services/adminService';

const pageOptions = [10, 25, 50, 100];

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString();
  } catch (e) {
    return String(value);
  }
};

const renderJson = (obj) => {
  if (!obj || typeof obj !== 'object') return '-';
  return JSON.stringify(obj, null, 2);
};

const AuditLogViewer = () => {
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [eventType, setEventType] = useState('all');
  const [entity, setEntity] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [expanded, setExpanded] = useState({});
  const [eventOptions, setEventOptions] = useState(['all']);
  const [entityOptions, setEntityOptions] = useState(['all']);
  const [filtersLoading, setFiltersLoading] = useState(false);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const { rows, count } = await AdminService.getAuditLogs({
        page,
        pageSize,
        eventType,
        entity,
        search,
        dateFrom,
        dateTo,
        sortBy,
        sortDir
      });
      setLogs(rows || []);
      setTotalCount(count || 0);
    } catch (e) {
      console.error('Audit log load error', e);
      setError('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [eventType, entity, search, dateFrom, dateTo, page, pageSize, sortBy, sortDir]);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        setFiltersLoading(true);
        const { eventTypes, entityTypes } = await AdminService.getAuditLogFilters();
        const evts = ['all'].concat(eventTypes || []);
        const ents = ['all'].concat(entityTypes || []);
        setEventOptions(evts);
        setEntityOptions(ents);
      } catch (e) {
        console.warn('Failed to load audit filters', e);
      } finally {
        setFiltersLoading(false);
      }
    };
    loadFilters();
  }, []);

  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / pageSize));

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('desc');
    }
  };

  const toggleExpanded = (id) => {
    setExpanded((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const exportCSV = async () => {
    try {
      const csv = await AdminService.exportAuditLogsCSV({
        eventType,
        entity,
        search,
        dateFrom,
        dateTo,
        sortBy,
        sortDir
      });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Failed to export audit logs');
    }
  };

  return (
    <div className="admin-page text-white font-mono">
      <div className="admin-header">
        <h3 className="admin-title">📋 Audit Logs</h3>
        <p className="admin-subtitle">Review system activity across admin actions.</p>
      </div>

      <div className="admin-card bg-gray-800 border-gray-700">
        <div className="admin-card-content">
          <div className="admin-grid-4">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Search summary / actor / entity id</label>
              <input
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="admin-input bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                placeholder="Search logs"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Event Type</label>
              <select value={eventType} onChange={(e) => { setEventType(e.target.value); setPage(1); }} className="admin-select bg-gray-700 border-gray-600 text-white">
                {eventOptions.map((et) => (
                  <option key={et} value={et}>{et === 'all' ? 'All events' : et}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Entity</label>
              <select value={entity} onChange={(e) => { setEntity(e.target.value); setPage(1); }} className="admin-select bg-gray-700 border-gray-600 text-white">
                {entityOptions.map((en) => (
                  <option key={en} value={en}>{en === 'all' ? 'All entities' : en}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Date From</label>
              <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="admin-input bg-gray-700 border-gray-600 text-white" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Date To</label>
              <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="admin-input bg-gray-700 border-gray-600 text-white" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Page Size</label>
              <select value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value, 10) || 25); setPage(1); }} className="admin-select bg-gray-700 border-gray-600 text-white">
                {pageOptions.map((opt) => <option key={opt} value={opt}>{opt} / page</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="admin-alert admin-alert-error">
          <p>{error}</p>
        </div>
      )}

      <div className="admin-actions">
        <button onClick={exportCSV} className="btn-sm">📊 Export CSV</button>
      </div>

      <div className="admin-card bg-gray-800 border-gray-700">
        <div className="overflow-x-responsive overflow-y-hidden">
          <table className="admin-table">
            <thead>
              <tr className="border-b border-gray-700 bg-gray-900/50">
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('created_at')}>Timestamp</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('event_type')}>Event</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('entity')}>Entity</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Summary</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actor</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700 text-white">
              {(loading || filtersLoading) && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-400">Loading…</td>
                </tr>
              )}
              {!loading && !filtersLoading && logs.map((log) => (
                <React.Fragment key={log.id}>
                  <tr className="hover:bg-gray-700/50">
                    <td className="px-4 py-2 text-xs text-gray-400">{formatDateTime(log.created_at)}</td>
                    <td className="px-4 py-2 text-xs text-white font-medium">{log.event_type}</td>
                    <td className="px-4 py-2 text-xs text-gray-300">{log.entity || '-'}</td>
                    <td className="px-4 py-2 text-xs text-gray-300">{log.summary || '-'}</td>
                    <td className="px-4 py-2 text-xs text-gray-400">{log.performed_by || '-'}</td>
                    <td className="px-4 py-2 text-xs">
                      <button
                        type="button"
                        onClick={() => toggleExpanded(log.id)}
                        className="btn-sm"
                      >{expanded[log.id] ? 'Hide Details' : 'View Details'}</button>
                    </td>
                  </tr>
                  {expanded[log.id] && (
                    <tr className="bg-gray-900/30">
                      <td colSpan={6} className="px-4 py-3">
                        <div className="grid md:grid-cols-2 gap-3 text-xs text-gray-400">
                          <div>
                            <div className="font-semibold text-gray-300 mb-1">Metadata</div>
                            <pre className="bg-gray-900 border border-gray-700 rounded p-3 overflow-auto max-h-56 whitespace-pre-wrap text-gray-300">
                              {renderJson(log.metadata)}
                            </pre>
                          </div>
                          <div>
                            <div className="font-semibold text-gray-300 mb-1">Identifiers</div>
                            <div className="space-y-1">
                              <div><span className="font-medium text-gray-300">Log ID:</span> {log.id}</div>
                              <div><span className="font-medium text-gray-300">Entity ID:</span> {log.entity_id || '-'}</div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {!loading && !filtersLoading && logs.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-400">No audit logs found for the selected filters.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="admin-pagination">
        <div>
          Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, totalCount)} of {totalCount || 0}
        </div>
        <div className="admin-pagination-buttons">
          <button disabled={page <= 1} onClick={() => setPage(1)} className="btn-sm disabled:opacity-50">First</button>
          <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="btn-sm disabled:opacity-50">Prev</button>
          <span>Page {page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="btn-sm disabled:opacity-50">Next</button>
          <button disabled={page >= totalPages} onClick={() => setPage(totalPages)} className="btn-sm disabled:opacity-50">Last</button>
        </div>
      </div>
    </div>
  );
};

export default AuditLogViewer;
